import React, { useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, VideoCamera, FilePdf, Paperclip, X } from "@phosphor-icons/react";

/**
 * MediaAttachments — manages a list of file attachments (images, videos, pdfs, etc.)
 * Renders thumbnails with remove buttons + an Upload button.
 *
 * `value` is an array of attachment objects: { file_id, url, content_type, name }
 * `onChange(next)` fires with the updated array.
 */
export function MediaAttachments({ value = [], onChange, label = "Attachments", compact = false, testIdPrefix = "media" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const postFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data;
  };

  const uploadMany = async (files) => {
    setUploading(true);
    let acc = value || [];
    try {
      for (const f of files) {
        // Serial upload so the per-institution quota check on the server sees each write in turn.
        // Thread the accumulator through the loop — do NOT read from the `value` closure inside
        // this async loop; React does not refresh closures of an in-flight function.
        const rec = await postFile(f);
        acc = [...acc, rec];
        onChange(acc);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) uploadMany(files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) uploadMany(files);
  };

  return (
    <div
      data-testid={`${testIdPrefix}-attachments`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={dragOver ? "outline outline-2 outline-zinc-900 outline-offset-2 bg-zinc-50 transition-colors" : ""}
    >
      {label && !compact && (
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">{label}</div>
      )}
      <div className={`flex flex-wrap gap-2 ${value.length > 0 ? "mb-2" : ""}`}>
        {value.map((a, i) => (
          <Thumb key={a.file_id || i} att={a} onRemove={() => remove(i)} testId={`${testIdPrefix}-att-${i}`} />
        ))}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,application/pdf"
        onChange={onFile}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`border border-dashed border-zinc-300 ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-xs"} hover:bg-zinc-50 inline-flex items-center gap-1.5 disabled:opacity-50`}
        data-testid={`${testIdPrefix}-upload-btn`}
      >
        <Paperclip size={12} />
        {uploading ? "Uploading…" : (dragOver ? "Drop to attach" : "Attach or drop files")}
      </button>
    </div>
  );
}

function Thumb({ att, onRemove, testId }) {
  const isImage = (att.content_type || "").startsWith("image/");
  const isVideo = (att.content_type || "").startsWith("video/");
  const isAudio = (att.content_type || "").startsWith("audio/");
  const isPdf = att.content_type === "application/pdf";
  const fullUrl = `${API_BASE}${att.url.replace(/^\/api/, "")}`;
  return (
    <div className="relative group border border-zinc-300 bg-zinc-50 w-24 h-24 flex items-center justify-center" data-testid={testId}>
      {isImage ? (
        <img src={fullUrl} alt={att.name || ""} className="max-w-full max-h-full object-contain" />
      ) : isVideo ? (
        <VideoCamera size={24} className="text-zinc-700" />
      ) : isAudio ? (
        <ImageIcon size={24} className="text-zinc-700" />
      ) : isPdf ? (
        <FilePdf size={24} className="text-red-700" />
      ) : (
        <Paperclip size={24} className="text-zinc-700" />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-2 -right-2 bg-zinc-900 text-white w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
        aria-label="Remove attachment"
      >
        <X size={10} weight="bold" />
      </button>
      <div className="absolute bottom-0 left-0 right-0 bg-zinc-900/80 text-white text-[9px] px-1 py-0.5 truncate" title={att.name}>
        {att.name || "file"}
      </div>
    </div>
  );
}

/**
 * MediaRenderer — read-only renderer for question & option attachments.
 */
export function MediaRenderer({ attachments = [], inline = false }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-3 ${inline ? "" : "mt-3"}`} data-testid="media-renderer">
      {attachments.map((a, i) => <MediaItem key={a.file_id || i} att={a} />)}
    </div>
  );
}

function MediaItem({ att }) {
  const ct = att.content_type || "";
  const fullUrl = `${API_BASE}${(att.url || "").replace(/^\/api/, "")}`;
  if (ct.startsWith("image/")) {
    return (
      <img
        src={fullUrl}
        alt={att.name || "Question attachment"}
        className="max-h-64 max-w-full border border-zinc-200"
      />
    );
  }
  if (ct.startsWith("video/")) {
    return (
      <video
        controls
        src={fullUrl}
        className="max-h-64 max-w-full border border-zinc-200 bg-black"
      />
    );
  }
  if (ct.startsWith("audio/")) {
    return <audio controls src={fullUrl} className="w-full" />;
  }
  if (ct === "application/pdf") {
    return (
      <a
        href={fullUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs hover:bg-zinc-50"
      >
        <FilePdf size={14} className="text-red-700" />
        {att.name || "PDF reference"}
      </a>
    );
  }
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs hover:bg-zinc-50"
    >
      <Paperclip size={14} />
      {att.name || "Attachment"}
    </a>
  );
}
